import { Check } from "@mui/icons-material";
import {
  Button,
  Chip,
  InputAdornment,
  ListItemIcon,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useState, useCallback, useMemo } from "react";

const MultiInput = ({
  options = [],
  initialValues = [],
  value,
  select = false,
  onChange,
  name,
  label,
  getLabelText,
  setFinalValues,
  ...props
}) => {
  const [inputValue, setInputValue] = useState("");

  const currentValues = useMemo(() => {
    return value || initialValues || [];
  }, [value, initialValues]);

  const resolvedValues = useMemo(() => {
    return currentValues.map((val) => {
      if (typeof val === "object" && val !== null) {
        return val;
      }
      if (options && options.length > 0) {
        const found = options.find((opt) => opt?._id === val || opt === val);
        if (found) return found;
      }
      return val;
    });
  }, [currentValues, options]);

  const isSelected = useCallback(
    (opt) => {
      const optId = typeof opt === "object" && opt !== null ? opt._id : opt;
      return resolvedValues.some((item) => {
        const itemId = typeof item === "object" && item !== null ? item._id : item;
        return itemId === optId;
      });
    },
    [resolvedValues]
  );

  const handleToggleValue = useCallback(
    (itemVal) => {
      const isObject = typeof itemVal === "object" && itemVal !== null;

      // Handle comma-separated strings
      if (!isObject && typeof itemVal === "string" && itemVal.includes(",")) {
        const parts = itemVal
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p !== "");
        
        let newVals = [...currentValues];
        for (const part of parts) {
          if (!newVals.includes(part)) {
            newVals.push(part);
          }
        }
        
        if (onChange) {
          onChange({ target: { name, value: newVals } });
        }
        if (setFinalValues) {
          setFinalValues((prev) => ({ ...prev, [name]: newVals }));
        }
        setInputValue("");
        return;
      }

      const exists = isObject
        ? currentValues.some((item) => {
            const itemId = typeof item === "object" && item !== null ? item._id : item;
            return itemId === itemVal._id;
          })
        : currentValues.includes(itemVal);

      const newVals = exists
        ? currentValues.filter((item) => {
            if (isObject) {
              const itemId = typeof item === "object" && item !== null ? item._id : item;
              return itemId !== itemVal._id;
            }
            return item !== itemVal;
          })
        : [...currentValues, itemVal];

      const returnValue = isObject
        ? newVals.map((item) => (typeof item === "object" && item !== null ? item._id : item))
        : newVals;

      if (onChange) {
        onChange({ target: { name, value: returnValue } });
      }
      if (setFinalValues) {
        const objectVals = newVals.map((item) => {
          if (typeof item === "object" && item !== null) return item;
          return options.find((opt) => opt?._id === item || opt === item) || item;
        });
        setFinalValues((prev) => ({ ...prev, [name]: objectVals }));
      }
      setInputValue("");
    },
    [currentValues, onChange, name, setFinalValues, options]
  );

  const renderLabel = useCallback(
    (item) => {
      if (typeof item !== "object") return item;
      return getLabelText
        ? getLabelText(item)
        : `${item?.name?.en} - ${item?.name?.hi}`;
    },
    [getLabelText]
  );

  const inputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          <Button
            onClick={() => handleToggleValue(inputValue)}
            variant="contained"
          >
            Add
          </Button>
        </InputAdornment>
      ),
    }),
    [inputValue, handleToggleValue]
  );

  const renderSelectField = useMemo(() => {
    return (
      <TextField
        {...props}
        fullWidth
        label={label}
        name={name}
        value=""
        select
        onChange={(e) => {
          const selectedOption = options?.find(
            (opt) => opt === e.target.value || opt._id === e.target.value
          );
          if (selectedOption) handleToggleValue(selectedOption);
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt._id || opt} value={opt}>
            {isSelected(opt) && (
              <ListItemIcon>
                <Check />
              </ListItemIcon>
            )}
            {renderLabel(opt)}
          </MenuItem>
        ))}
      </TextField>
    );
  }, [handleToggleValue, label, name, options, renderLabel, isSelected, props]);

  const renderInputField = useMemo(() => {
    return (
      <TextField
        {...props}
        fullWidth
        label={label}
        name={name}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleToggleValue(inputValue);
          }
        }}
        InputProps={inputProps}
      />
    );
  }, [handleToggleValue, inputValue, inputProps, label, name, props]);

  return (
    <>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {resolvedValues?.map((item) => (
          <Chip
            key={item._id || item}
            label={renderLabel(item)}
            variant="outlined"
            onDelete={() => handleToggleValue(item)}
          />
        ))}
      </Stack>

      {select && options.length > 0 ? renderSelectField : renderInputField}
    </>
  );
};

export default MultiInput;
